"use client";
import React, { useMemo } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { useTranslations } from "next-intl";
import classNames from "classnames";
import { useTheme } from "@/context/Theme.context";

import { ShowWhen } from "@/components/common";
import NavArrowIcon from "@/components/assets/icons/parcel-nav-arrow.svg";

import { ParcelStatus, Theme } from "@/enums";
import { getTextClass } from "./actions";
import {
  ArrivedIcon,
  ExpectedParcelsIcon,
  InTransitIcon,
  WarehouseIcon,
} from "@/components/assets/icons";
import { useParcelsRequest } from "@/api/useParcelsRequest";
import { parcelsStatusses } from "@/utils/additional";

const ParcelsNavigation = () => {
  const pathname = usePathname();

  const t = useTranslations();

  const theme = useTheme();

  const { data } = useParcelsRequest();
  const parcelsData = data?.data?.data || [];

  const navItems = useMemo(() => {
    return {
      getParcel: [
        {
          title: "expected_parcels",
          href: "/parcels/get-parcel/expected-parcels",
          icon: ExpectedParcelsIcon,
          quantity:
            parcelsData.filter(
              (parcel) =>
                parcel.commonStatusCode ===
                ParcelStatus.COMMON_STATUS_STATUS_WAITING_TO_RECEIVE,
            )?.length || 0,
        },
        {
          title: "warehouse",
          href: "/parcels/get-parcel/warehouse",
          icon: WarehouseIcon,
          quantity:
            parcelsData?.filter((item) =>
              parcelsStatusses.warehouse.includes(item?.commonStatusCode),
            )?.length || 0,
        },
        {
          title: "in_transit",
          href: "/parcels/get-parcel/in-transit",
          icon: InTransitIcon,
          quantity:
            parcelsData?.filter((item) =>
              parcelsStatusses.inTransit.includes(item?.commonStatusCode),
            )?.length || 0,
        },
        {
          title: "arrived",
          href: "/parcels/get-parcel/arrived",
          icon: ArrivedIcon,
          quantity:
            parcelsData?.filter((item) =>
              parcelsStatusses.arrived.includes(item?.commonStatusCode),
            )?.length || 0,
        },
      ],
      sendParcel: [
        {
          title: "order_created",
          href: "/parcels/send-parcel/order-created",
          icon: ExpectedParcelsIcon,
          quantity: 0,
        },
        {
          title: "warehouse",
          href: "/parcels/send-parcel/warehouse",
          icon: WarehouseIcon,
          quantity: 0,
        },
        {
          title: "in_transit_send",
          href: "/parcels/send-parcel/in-transit",
          icon: InTransitIcon,
          quantity: 0,
        },
        {
          title: "delivered_send",
          href: "/parcels/send-parcel/arrived",
          icon: ArrivedIcon,
          quantity: 0,
        },
      ],
    };
  }, [parcelsData]);

  const isActive = (href: string) => {
    return pathname === href;
  };

  const isSendParcel = pathname.includes("send-parcel");

  const navMenu = isSendParcel ? navItems.sendParcel : navItems.getParcel;

  return (
    <div className="flex w-full justify-start">
      {navMenu.map((item, index) => {
        const Icon = item.icon;
        return (
          <Link
            key={index}
            href={item.href}
            className={classNames(
              `space-x-2f relative flex h-[76px] w-full cursor-pointer items-center rounded-lg py-2 pl-10 pr-4`,
              {
                "pl-4 pr-8": index === 0,
                "pl-12 pr-6": index === 1 || index === 2,
                "pl-12 pr-4": index === 3,
                "bg-[#e7f6f8]": isActive(item.href) && theme !== Theme.UK,
                "bg-[#ffe4d1]": isActive(item.href) && theme === Theme.UK,
                "bg-[#f9f9f9]": !isActive(item.href),
              },
            )}
          >
            <ShowWhen condition={index < 3}>
              <span className="absolute right-[-40px] top-[-15px] z-20">
                <NavArrowIcon
                  className={classNames({
                    "fill-[#e7f6f8]": isActive(item.href) && theme !== Theme.UK,
                    "fill-[#ffe4d1]": isActive(item.href) && theme === Theme.UK,
                    "fill-[#f9f9f9]": !isActive(item.href),
                  })}
                />
              </span>
            </ShowWhen>
            <div className="flex w-full justify-between gap-4">
              <div className="h-1f4 w-1f4 flex items-center justify-center rounded-full bg-white p-2">
                <Icon
                  className={classNames({
                    "fill-[#13cfe8]": isActive(item.href) && theme !== Theme.UK,
                    "fill-[#eb7623]": isActive(item.href) && theme === Theme.UK,
                  })}
                />
              </div>
              <div className="flex w-full items-center justify-between">
                <span
                  className={classNames(
                    "line-clamp-2 hidden truncate text-wrap text-base font-semibold leading-tight lg:inline",
                    getTextClass({
                      isActive: isActive(item.href),
                      theme,
                    }),
                  )}
                >
                  {t(item.title)}
                </span>
                <div className="absolute right-[10px] z-50 flex h-9 w-9 items-center justify-center rounded-full bg-white">
                  <span
                    className={classNames(
                      "p-3 text-lg font-semibold",
                      getTextClass({
                        isActive: isActive(item.href),
                        theme,
                      }),
                    )}
                  >
                    {item.quantity}
                  </span>
                </div>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default ParcelsNavigation;
